/** @jsxRuntime classic */
/** @jsx jsx */

import { ReactNode } from 'react';
import { css, jsx } from '@emotion/react';
import { labelStyles } from '@zapier/style-encapsulation';
import { Colors, Typography } from '@zapier/design-system';
import { COLOR_KEYS, MEDIA_MIN_WIDTH } from '../../../constants';
import { RichTextHtml } from '../../RichTextHtml/RichTextHtml';
import { WistiaVideo } from '../../WistiaVideo/WistiaVideo';
import { RichTextHTMLString } from '../types';

export interface VideoSectionProps {
  backgroundColor:
    | typeof COLOR_KEYS.moss
    | typeof COLOR_KEYS.neutral800
    | typeof COLOR_KEYS.night
    | typeof COLOR_KEYS.zapier;
  description?: RichTextHTMLString;
  heading: string;
  wistiaId?: string;
  children?: ReactNode;
}

const Styles = labelStyles('VideoSection', {
  root: (backgroundColor: VideoSectionProps['backgroundColor']) =>
    css({
      padding: '60px 20px',
      backgroundColor: Colors[backgroundColor],

      [MEDIA_MIN_WIDTH.large]: {
        padding: '60px 20px 80px',
      },
    }),

  container: css({
    maxWidth: '1170px',
    margin: '0 auto',
  }),

  content: css({
    maxWidth: '512px',
    margin: '0 auto 32px',

    [MEDIA_MIN_WIDTH.small]: {
      margin: '0 auto 40px',
    },

    [MEDIA_MIN_WIDTH.large]: {
      maxWidth: '800px',
      margin: '0 auto 48px',
    },
  }),

  heading: css({
    ...Typography.pageHeader7,
    color: Colors.neutral100,
    textAlign: 'center',
    marginBottom: '8px',

    [MEDIA_MIN_WIDTH.small]: {
      ...Typography.pageHeader5,
      marginBottom: '16px',
    },

    [MEDIA_MIN_WIDTH.large]: {
      ...Typography.pageHeader3,
      marginBottom: '20px',
    },
  }),

  description: css({
    ...Typography.heading,
    fontSize: '16px',
    lineHeight: '22px',
    textAlign: 'center',
    color: Colors.neutral100,

    [MEDIA_MIN_WIDTH.small]: {
      ...Typography.subHeader5,
    },

    [MEDIA_MIN_WIDTH.large]: {
      ...Typography.subHeader3,
    },
  }),
});

export function VideoSection(props: VideoSectionProps) {
  return (
    <div css={Styles.root(props.backgroundColor)}>
      <div css={Styles.container}>
        <div css={Styles.content}>
          <h2 css={Styles.heading}>{props.heading}</h2>
          {props.description && (
            <div css={Styles.description}>
              <RichTextHtml html={props.description} />
            </div>
          )}
        </div>
        {props.wistiaId ? (
          <WistiaVideo wistiaId={props.wistiaId} />
        ) : (
          props.children
        )}
      </div>
    </div>
  );
}
